import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Play, Pause, SkipBack, SkipForward, Volume2, Captions, Loader2, XCircle, Maximize, Minimize, FastForward, ChevronRight } from 'lucide-react';
import { useSkipOptionalItem, useStartItem, useStopItem, useStoreWatchTimeTrack, useUpsertWatchTime } from '../hooks/hooks';
import { fetchClient } from '@/lib/openapi';


import { useCourseStore } from '../store/course-store';
import { usePlayerStore } from '../store/player-store'; // Import the new store
import type { VideoProps, VideoRef, YTPlayerInstance } from '@/types/video.types';
import { PLAYER_STATE, createHlsPlayerInstance } from './video-players/hlsPlayerInstance';
import { resolveVideoSource } from '@/types/media.types';
import { getVideoPlaybackUrl } from '@/lib/api/media';

import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { WatchTimeTrackData } from '@/types/user_activity_event.types';


// Helper to format seconds to HH:MM:SS
function formatSecondsToTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Short, encouraging lines shown while the player + proctoring environment load.
const LOADING_QUOTES: string[] = [
  "The beautiful thing about learning is that no one can take it away from you.",
  "Every expert was once a beginner.",
  "Small steps every day add up to big results.",
  "Learning never exhausts the mind — it only lights it up.",
  "Focus on the step in front of you, not the whole staircase.",
  "Curiosity is the engine of achievement.",
  "Mistakes are proof that you are trying.",
  "An investment in knowledge pays the best interest.",
];

// Helper to extract YouTube video ID from URL
function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:v=|youtu.be\/?)([\w-]{11})/);
  return match ? match[1] : null;
}

// Helper to parse time string (HH:MM:SS or MM:SS or SS) to seconds
function parseTimeToSeconds(timeStr: string): number {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2]; // HH:MM:SS
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1]; // MM:SS
  } else {
    return parts[0] || 0; // SS
  }
}

const Video = forwardRef<VideoRef, VideoProps>(function Video({ URL, source, assetId, startTime, nextItemId, endTime, points, anomalies, readyToDetect, rewindVid, pauseVid, doGesture = false, onNext, isProgressUpdating, onDurationChange, keyboardLockEnabled = true, focusMode = false, linearProgressionEnabled, seekForwardEnabled, isCompleted, isAlreadyWatched, completedItemIdsRef, pauseSignal, awayPaused = false }: VideoProps, ref) {
  /**
   * An uploaded lesson streams HLS instead of embedding YouTube. Everything else
   * in this component — proctoring, seek gating, watch-time, overlays, keyboard
   * locks — is identical for both, because both players expose the same interface.
   */
  const isUploadedVideo = resolveVideoSource({ source }) === 'GCS' && Boolean(assetId);
  const playerRef = useRef<YTPlayerInstance | null>(null);
  const iframeRef = useRef<HTMLDivElement>(null);
  const stopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSeekTimeRef = useRef<number>(0);
  const lastSeekErrorToastRef = useRef<number>(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // const [volume, setVolume] = useState(100);
  // Use the stored playback rate from the player store
  const { playbackRate, setPlaybackRate, volume, setVolume, subtitlesEnabled, setSubtitlesEnabled } = usePlayerStore();
  const [maxTime, setMaxTime] = useState(0);
  const [videoEnded, setVideoEnded] = useState(false);
  /**
   * Selectable renditions reported by whichever player is active. Read once the
   * player is ready rather than on every render, since for HLS the ladder is only
   * known after the manifest parses.
   */
  const [qualityLevels, setQualityLevels] = useState<string[]>([]);
  const [selectedQuality, setSelectedQuality] = useState('auto');
  // Rotating quote shown during the "preparing environment" delay.
  const [quoteIndex, setQuoteIndex] = useState(0);
  useEffect(() => {
    if (readyToDetect) return;
    const id = setInterval(() => {
      setQuoteIndex((i) => (i + 1) % LOADING_QUOTES.length);
    }, 4000);
    return () => clearInterval(id);
  }, [readyToDetect]);
  const videoId = getYouTubeId(URL);
  const { currentCourse, setWatchItemId } = useCourseStore();
  const startItem = useStartItem();
  const stopItem = useStopItem();
  const upsertWatchTime = useUpsertWatchTime();
  const isStopping = stopItem.isPending;
  const stopError = stopItem.error;
  const { mutateAsync: storeWatchTimeTrack } = useStoreWatchTimeTrack();

  // Parse start and end times
  const startTimeSeconds = parseTimeToSeconds(startTime || '0');
  const endTimeSeconds = parseTimeToSeconds(endTime || '');

  const progressStartedRef = useRef(false);
  const progressStoppedRef = useRef(false);
  const watchItemIdRef = useRef<string | null>(null);
  const stopInFlightRef = useRef(false);
  const stopFailCountRef = useRef(0);
   const nextItemIdRef = useRef(nextItemId);
  useEffect(() => {
    nextItemIdRef.current = nextItemId;
  }, [nextItemId]);

  // subtitlesEnabled is persisted in the player store (see usePlayerStore above).
  /**
   * Only the YouTube player can show subtitles. The uploaded-video player's
   * caption methods are no-ops and no WebVTT track is produced upstream, so the
   * control is hidden there instead of toggling a preference that does nothing.
   *
   * Whether a *particular* YouTube video carries captions can't be known without
   * loading the captions module (which would display them), so that is checked
   * at toggle time rather than up front — see handleToggleSubtitles.
   */
  const subtitlesSupported = !isUploadedVideo && Boolean(videoId);
  /** Pending caption-availability poll, cleared on re-toggle and on teardown. */
  const captionProbeRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => () => {
    if (captionProbeRef.current) clearTimeout(captionProbeRef.current);
  }, []);

  /** Height of the strip that covers YouTube's title/share chrome — see below. */
  const YT_CHROME_COVER_PX = 64;
  const ytIframeRef = useRef<HTMLIFrameElement | null>(null);

  /**
   * Sizes the YouTube iframe, which has to be framed one of two ways.
   *
   * Captions OFF — the iframe is grown 72px past the top and bottom of its
   * (overflow-hidden) container. On a 16:9 stage YouTube letterboxes the extra
   * height, so the video still fills the container exactly and it is only the
   * black bars — and the chrome drawn on them — that get clipped. Nothing of the
   * picture is lost, which is why this stays the default.
   *
   * Captions ON — that same clipping is what hides the captions, because YouTube
   * draws them into the bottom bar. So the iframe is left at container size: with
   * no letterboxing the captions land over the bottom of the picture itself,
   * where they are visible. YouTube's top chrome is then inside the frame too and
   * is covered by an opaque strip instead (see the overlay in the markup).
   *
   * The cost of the captions-on framing is the strip of picture behind that
   * cover, which is why it is not applied unless subtitles are actually on.
   */
  const applyIframeFraming = (iframeEl: HTMLIFrameElement, captionsOn: boolean) => {
    iframeEl.style.top = captionsOn ? '0px' : '-72px';
    iframeEl.style.height = captionsOn ? '100%' : 'calc(100% + 144px)';
  };

  useEffect(() => {
    if (ytIframeRef.current) applyIframeFraming(ytIframeRef.current, subtitlesEnabled);
  }, [subtitlesEnabled, playerReady]);

  // const [videoEnded, setVideoEnded] = useState(false);

  // Track if video was playing before gesture pause
  const wasPlayingBeforeGesture = useRef(false);

  // Track if video was playing before rewind pause
  const wasPlayingBeforeRewind = useRef(false);

  // Track if rewind has been processed to prevent multiple triggers
  const rewindProcessedRef = useRef(false);

  // Track if we've already auto-played the video
  const hasAutoPlayedRef = useRef(false)

  // Track maxTime with a ref for synchronous updates (state updates are async)
  const maxTimeRef = useRef(startTimeSeconds);

  // Track grace period completion
  const [gracePeriodCompleted, setGracePeriodCompleted] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Auto-hiding control bar.
   *
   * Only applies where the bar floats over the picture. Docked below the video it
   * occupies layout, so hiding it would reflow the stage and shift the video —
   * worse than leaving it. Overlaid, it covers the picture, which is what makes
   * getting out of the way worth doing.
   */
  const controlsOverlaid = focusMode || isFullscreen;
  /** How far up from the bottom edge the cursor reveals the bar. */
  const CONTROLS_REVEAL_ZONE_PX = 140;
  const CONTROLS_IDLE_MS = 2200;
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  /** Set while the cursor rests on the bar, which suspends the hide timer. */
  const pointerOverControlsRef = useRef(false);

  const clearControlsHideTimer = () => {
    if (controlsHideTimerRef.current) {
      clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
    }
  };

  /**
   * Hides the bar after a spell without interaction — but never while paused (a
   * paused player with no controls reads as broken rather than clean) and never
   * while the cursor is resting on the bar itself.
   */
  const scheduleControlsHide = () => {
    clearControlsHideTimer();
    if (!controlsOverlaid || !playing || pointerOverControlsRef.current) return;
    controlsHideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_IDLE_MS);
  };

  const revealControls = () => {
    setControlsVisible(true);
    scheduleControlsHide();
  };

  /** Reveals the bar when the cursor comes down into the strip above it. */
  const handleStageMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!controlsOverlaid) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.bottom - event.clientY <= CONTROLS_REVEAL_ZONE_PX) revealControls();
  };

  // Pausing brings the bar back; playing starts it counting down again.
  useEffect(() => {
    if (!controlsOverlaid || !playing) {
      clearControlsHideTimer();
      setControlsVisible(true);
      return;
    }
    scheduleControlsHide();
    return clearControlsHideTimer;
  }, [playing, controlsOverlaid]);

  useEffect(() => clearControlsHideTimer, []);

  // Watch time tracking state
  const [watchTimeTrack, setWatchTimeTrack] = useState<WatchTimeTrackData>({
    rewinds: 0,
    fastForwards: 0,
    videoId: '', // Will be set when currentCourse is available
    userId: '',
    courseId: '',
    versionId: '',
    cohortId: undefined, // Will be set when currentCourse is available
    rewindData: [],
    fastForwardData: []
  });
  const watchTimeTrackRef = useRef<WatchTimeTrackData>(watchTimeTrack);

  const wasPlayingBeforeTabSwitch = useRef(false);


  useEffect(() => {
    watchTimeTrackRef.current = watchTimeTrack;
  }, [watchTimeTrack]);

  const sendWatchTimeTrackData = async (capturedTrackData?: WatchTimeTrackData) => {

    try {

      // Use captured data if provided, otherwise use current state
      const dataToSend = capturedTrackData || watchTimeTrackRef.current;

      // Get user ID from current course or localStorage
      const userId = currentCourse?.userId || localStorage.getItem('userId') || '';

      const trackData: WatchTimeTrackData = {
        ...dataToSend,
        userId: userId,
        courseId: currentCourse?.courseId || '',
        versionId: currentCourse?.versionId || '',
        cohortId: currentCourse?.cohortId || undefined,
        // Don't override videoId - use the tracked one
      };
      await storeWatchTimeTrack({ body: trackData });

      // Reset tracking after successful send
      setWatchTimeTrack({
        rewinds: 0,
        fastForwards: 0,
        videoId: currentCourse?.itemId || '', // Use course itemId instead of YouTube video ID
        userId: '',
        courseId: '',
        versionId: '',
        cohortId: undefined,
        rewindData: [],
        fastForwardData: []
      });

    } catch (error) {
      console.error(' [DEBUG] Failed to send watch time track data:', error);
    }
  };

  // Track previous time for seek detection
  const previousTimeRef = useRef(0);
  const seekDetectionRef = useRef(false);
  const captureInProgressRef = useRef(false);

  // HANDLE STOP FAILED CASE, SHOW SKIP OPTION IF FAILED
  const [isStopFailed, setIsStopFailed] = useState(false);
  const { mutateAsync: skipItemAsync, isPending: isSkipping } = useSkipOptionalItem();


  const handleSkipItem = async () => {
    if (!currentCourse?.itemId) return;
    try {

      await skipItemAsync({ params: { path: { itemId: currentCourse?.itemId } } });
      // toast.success('Item skipped successfully');
      handlePlayPause()
      console.log("Handle skip called stop API....")
      onNext?.();
    } catch (error) {
      console.error('Error skipping item:', error);
      toast.error('Failed to skip item');
    }
  };

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        // Fullscreen the document root so page-level proctoring overlays stay visible.
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
      toast.error('Failed to toggle fullscreen');
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Sync once on mount: if the player mounts while the browser is already in
    // fullscreen (e.g. navigating between videos without leaving fullscreen),
    // no change event fires, so the button would otherwise show the wrong state.
    handleFullscreenChange();

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Pause video when user switches browser tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      const player = playerRef.current;
      if (!player) return;

      if (document.hidden) {
        if (playing) {
          wasPlayingBeforeTabSwitch.current = true;
          player.pauseVideo();
        } else {
          wasPlayingBeforeTabSwitch.current = false;
        }
      } else {
        if (wasPlayingBeforeTabSwitch.current && playerReady) {
          player.playVideo();
          setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
          wasPlayingBeforeTabSwitch.current = false;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [playing, playerReady, playbackRate]);

  // Wait 10 seconds after readyToDetect becomes true (to match FloatingVideo's grace period)
  useEffect(() => {
    if (readyToDetect && !gracePeriodCompleted) {
      const timer = setTimeout(() => {
        setGracePeriodCompleted(true);
      }, 10000); // 10 seconds to match FloatingVideo's grace period

      return () => clearTimeout(timer);
    }
  }, [readyToDetect, gracePeriodCompleted]);

  // Reset when video changes
  useEffect(() => {
    setGracePeriodCompleted(false);
    hasAutoPlayedRef.current = false; // Reset autoplay flag for new video
    maxTimeRef.current = startTimeSeconds; // Reset maxTime ref
    stopFailCountRef.current = 0; // Reset stop-failure retry counter

    // Update watchTimeTrack with course info when video changes
    if (currentCourse?.itemId) {
      setWatchTimeTrack(prev => ({
        ...prev,
        videoId: currentCourse.itemId || '',
        courseId: currentCourse.courseId || '',
        versionId: currentCourse.versionId || '',
        cohortId: currentCourse.cohortId || undefined,
      }));
    }
  }, [videoId, startTimeSeconds, currentCourse?.itemId, currentCourse?.courseId, currentCourse?.versionId, currentCourse?.cohortId]);

  // // Ensure video doesn't autoplay accidentally
  // useEffect(() => {
  //   if (playerReady && playerRef.current) {
  //     // Force pause when player becomes ready
  //     playerRef.current.pauseVideo();
  //     console.log('🔒 Safety: Video forced to paused state');
  //   }
  // }, [playerReady]);

  useEffect(() => {
    playerRef.current?.setPlaybackRate?.(playbackRate);
  }, [playbackRate]);

  // Control handlers
  const handlePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (!player || typeof player.pauseVideo !== 'function' || stopInFlightRef.current) return;
    if (playing || isSkipping || isStopFailed || isStopping) {
      player.pauseVideo();
    } else {
      // Prevent playing if current time is at or beyond endTime
      if (endTimeSeconds > 0 && currentTime >= endTimeSeconds) {
        return;
      }else{
        player.playVideo();
      }
      setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
    }
  }, [playing, endTimeSeconds, currentTime, isSkipping, isStopFailed, isStopping, playbackRate]);

  const handleBackward = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    const previousTime = currentTime;
    const newTime = Math.max(startTimeSeconds, currentTime - 10);
    player.seekTo(newTime, true);

    // Track the rewind
    setWatchTimeTrack(prev => {
      const newTrack = {
        ...prev,
        rewinds: prev.rewinds + 1,
        rewindData: [...prev.rewindData, {
          from: formatSecondsToTime(previousTime),
          to: formatSecondsToTime(newTime),
          createdAt: new Date().toISOString()
        }]
      };
      return newTrack;
    });
  };

  const handleForward = () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    // Allow forward seek if either the video is completed OR seek forward is enabled in settings
    if (!seekForwardEnabled) {
      return;
    }

    const previousTime = currentTime;
    const maxSeekTime = endTimeSeconds > 0 ? endTimeSeconds : duration;
    const newTime = Math.min(maxSeekTime, currentTime + 10);
    player.seekTo(newTime, true);

    // Track the fast forward
    setWatchTimeTrack(prev => {
      const newTrack = {
        ...prev,
        fastForwards: prev.fastForwards + 1,
        fastForwardData: [...prev.fastForwardData, {
          from: formatSecondsToTime(previousTime),
          to: formatSecondsToTime(newTime),
          createdAt: new Date().toISOString()
        }]
      };
      return newTrack;
    });
  };

  //  function to handle stop with debouncing
  const handleStopItem = useCallback(async (watchItemId: string | null, debounceMs: number = 0): Promise<boolean> => {
    // Clear any pending stop request
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    // Prevent duplicate concurrent requests
    if (stopInFlightRef.current) {
      console.log('Stop request already in flight, skipping');
      return false;
    }

    return new Promise((resolve) => {
      const executeStop = async () => {
        stopInFlightRef.current = true;
        try {
          if (watchItemId && !isAlreadyWatched && !(currentCourse!.itemId && completedItemIdsRef.current.has(currentCourse!.itemId)) && !isCompleted) {
            await stopItem.mutateAsync({
              params: {
                path: {
                  courseId: currentCourse!.courseId,
                  courseVersionId: currentCourse!.versionId ?? '',
                },
              },
              body: {
                watchItemId,
                itemId: currentCourse!.itemId ?? '',
                moduleId: currentCourse!.moduleId ?? '',
                sectionId: currentCourse!.sectionId ?? '',
                seekForwardEnabled,
                nextItemId: nextItemIdRef.current,
                cohortId: currentCourse!.cohortId || undefined,
              },
            });
            if (currentCourse?.itemId) {
              completedItemIdsRef.current.add(currentCourse!.itemId);
            }
          }
          if (!currentCourse?.itemId) return;
          progressStoppedRef.current = true;
          resolve(true);

       } catch (err: any) {
          console.error('Stop item failed:', err);

          // A failure here doesn't always mean the save actually failed - a
          // slow response can get its confirmation cut off mid-transfer
          // (surfaces as a JSON parse error) even though the backend already
          // committed the write. Check the server's actual state before
          // treating this as a real failure, instead of guessing.
          try {
            const { data: freshProgress } = await fetchClient.GET(
              '/users/progress/courses/{courseId}/versions/{courseVersionId}' as any,
              {
                params: {
                  path: {
                    courseId: currentCourse!.courseId,
                    courseVersionId: currentCourse!.versionId ?? '',
                  },
                  query: { cohortId: currentCourse!.cohortId || undefined },
                },
              },
            ) as { data?: { currentItem?: string } };
            if (freshProgress && freshProgress.currentItem !== currentCourse?.itemId) {
              // Progress already moved past this item - the earlier call
              // actually succeeded, we just didn't get to hear back about it.
              if (currentCourse?.itemId) {
                completedItemIdsRef.current.add(currentCourse.itemId);
              }
              progressStoppedRef.current = true;
              resolve(true);
              return;
            }
          } catch (verifyErr) {
            console.error('Progress verification failed:', verifyErr);
            // Fall through to the existing retry/failure handling below.
          }

          stopFailCountRef.current += 1;
          // Let the polling interval retry a few times before giving up, so a
          // transient/slow failure on a genuinely-watched video can still complete.
          if (stopFailCountRef.current >= 3) {
            progressStoppedRef.current = true;
            /**
             * The server's message is preferred, and read from several shapes
             * because different clients here wrap errors differently — the
             * previous single lookup missed most of them and fell through to the
             * watch-time text, so an unrelated 500 was reported to the learner as
             * "watch 30 seconds", sending everyone looking in the wrong place.
             */
            const serverMessage =
              err?.response?.data?.message ?? err?.data?.message ?? err?.message;
            toast.warning(
              serverMessage ||
              'We could not record your progress for this video. Please try again.',
            );
            setIsStopFailed(true);
          }
          resolve(false);

        } finally {
          stopInFlightRef.current = false;
          stopTimeoutRef.current = null;
        }
      };

      if (debounceMs > 0) {
        stopTimeoutRef.current = setTimeout(executeStop, debounceMs);
      } else {
        executeStop();
      }
    });
  }, [currentCourse, stopItem, isAlreadyWatched, completedItemIdsRef]);

  /**
   * Best-effort stop for a session that was started but never explicitly
   * closed. Shared by the imperative handle below (used by course-page.tsx's
   * handleNext/handlePrevVideo before navigating) and by the unmount effect
   * further down (a fallback for paths that skip that call entirely — e.g.
   * sidebar navigation, which today only awaits stopCurrentItem for BLOG
   * items). A no-op if nothing was started, or if stop already ran.
   */
  const stopIfInProgress = useCallback(async () => {
    const watchItemId = watchItemIdRef.current;
    if (
      progressStartedRef.current &&
      !progressStoppedRef.current &&
      !stopInFlightRef.current &&
      watchItemId
    ) {
      await handleStopItem(watchItemId, 0);
    }
  }, [handleStopItem]);

  useImperativeHandle(ref, () => ({
    stopItem: () => stopIfInProgress(),
  }), [stopIfInProgress]);

  // Pause/resume video based on doGesture
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (doGesture) {
      // Pause and remember if it was playing
      if (playing) {
        wasPlayingBeforeGesture.current = true;
        player.pauseVideo();
      } else {
        wasPlayingBeforeGesture.current = false;
      }
    } else {
      // Resume if it was playing before gesture
      if (wasPlayingBeforeGesture.current) {
        player.playVideo();
        setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
        wasPlayingBeforeGesture.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doGesture, playing]);

  // Handle rewind functionality
  useEffect(() => {
    if (rewindVid && !rewindProcessedRef.current) {
      const player = playerRef.current;
      if (!player) return;

      // Store current playing state before rewind
      wasPlayingBeforeRewind.current = playing;
      const newTime = startTimeSeconds;
      if (!player) return;
      player.seekTo?.(newTime, true);
      rewindProcessedRef.current = true;
    } else if (!rewindVid) {
      // Reset the flag when rewindVid becomes false
      rewindProcessedRef.current = false;
    }
  }, [rewindVid, currentTime, startTimeSeconds, playing]);

  // Handle pause functionality for anomaly detection
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    if (pauseVid) {
      // Pause video due to anomaly detection
      if (playing) {
        player.pauseVideo();
        wasPlayingBeforeRewind.current = true; // Remember it was playing
      }
    } else {
      // Resume video when anomalies are cleared
      // Only resume if it was playing before the rewind/pause
      if (wasPlayingBeforeRewind.current) {
        player.playVideo();
        setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
        wasPlayingBeforeRewind.current = false; // Reset the flag
      }
    }
  }, [pauseVid, playing]);

  // Imperative pause from the focused learn UI (clicking a floating control).
  // Pauses without showing the anomaly overlay. Skips the initial mount value so
  // the video isn't paused before it ever plays.
  const pauseSignalSeenRef = useRef<number | undefined>(pauseSignal);
  useEffect(() => {
    if (pauseSignal === undefined) return;
    if (pauseSignalSeenRef.current === pauseSignal) return;
    pauseSignalSeenRef.current = pauseSignal;
    playerRef.current?.pauseVideo?.();
  }, [pauseSignal]);

  // "Stepped away" pause (cursor left the page). Remembers whether the video was
  // playing and AUTO-RESUMES when the learner returns — but only if it was
  // actually playing and nothing else (anomaly/gesture) is blocking playback.
  const wasPlayingBeforeAway = useRef(false);
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (awayPaused) {
      if (playing) {
        player.pauseVideo();
        wasPlayingBeforeAway.current = true;
      }
    } else if (wasPlayingBeforeAway.current) {
      wasPlayingBeforeAway.current = false;
      if (!pauseVid && !rewindVid && !doGesture) {
        player.playVideo();
        setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
      }
    }
  }, [awayPaused, playing]);



  // Autoplay: Wait for grace period completion
  useEffect(() => {
    const player = playerRef.current;

    // Only auto-play if ALL conditions are perfect:
    // 1. Player is ready
    // 2. Camera permissions granted AND grace period completed
    // 3. Video is not already playing
    // 4. Not blocked by any anomalies
    // 5. We haven't auto-played yet
    if (playerReady &&
      readyToDetect &&
      gracePeriodCompleted && // Wait for grace period
      player &&
      !playing &&
      !pauseVid &&
      !rewindVid &&
      !doGesture &&
      !awayPaused &&
      !hasAutoPlayedRef.current &&
      !videoEnded) {


      const timer = setTimeout(() => {
        if (playerRef.current &&
          !playing &&
          !pauseVid &&
          !rewindVid &&
          !doGesture &&
          !awayPaused) {

          playerRef.current.playVideo();
          setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
          hasAutoPlayedRef.current = true;
        }
      }, 1000); // 1 second final delay

      return () => clearTimeout(timer);
    }
  }, [playerReady, readyToDetect, gracePeriodCompleted, playing, pauseVid, rewindVid, doGesture, awayPaused, videoEnded]);

  // Autoplay: Only trigger once when everything becomes ready
  // useEffect(() => {
  //   const player = playerRef.current;

  //   // Only auto-play if ALL conditions are perfect:
  //   // 1. Player is ready
  //   // 2. Camera permissions granted (readyToDetect = true after grace period)
  //   // 3. Video is not already playing
  //   // 4. Not blocked by any anomalies (pauseVid, rewindVid, doGesture)
  //   // 5. We haven't auto-played yet
  //   if (playerReady && 
  //       readyToDetect && 
  //       player && 
  //       !playing && 
  //       !pauseVid && 
  //       !rewindVid && 
  //       !doGesture &&
  //       !hasAutoPlayedRef.current) {

  //     console.log('🎬 Auto-playing video: All conditions met');

  //     // Small delay to ensure everything is settled
  //     const timer = setTimeout(() => {
  //       if (playerRef.current && 
  //           !playing && 
  //           !pauseVid && 
  //           !rewindVid && 
  //           !doGesture) {

  //         playerRef.current.playVideo();
  //         setTimeout(() => { playerRef.current?.setPlaybackRate?.(playbackRate); }, 50);
  //         hasAutoPlayedRef.current = true;
  //         console.log('✅ Video auto-played successfully');
  //       }
  //     }, 1000); // 1 second delay

  //     return () => clearTimeout(timer);
  //   }
  // }, [playerReady, readyToDetect, playing, pauseVid, rewindVid, doGesture]);

  // // Reset auto-play flag when video changes
  // useEffect(() => {
  //   hasAutoPlayedRef.current = false;
  // }, [videoId]);

  // Debug anomalies
  // useEffect(() => {
  //   if (anomalies && anomalies.length > 0) {
  //     console.log('🔍 [Video] Current anomalies:', anomalies);
  //   }
  // }, [anomalies]);
  // Handle keyboard events including space for play/pause



  function handleSendStartItem() {

    if (!currentCourse?.itemId) return;
    if (!isAlreadyWatched && !completedItemIdsRef.current.has(currentCourse!.itemId) && !isCompleted) {
      startItem.mutate({
        params: {
          path: {
            courseId: currentCourse.courseId,
            courseVersionId: currentCourse.versionId ?? '',
          },
        },
        body: {
          itemId: currentCourse.itemId,
          moduleId: currentCourse.moduleId ?? '',
          sectionId: currentCourse.sectionId ?? '',
          cohortId: currentCourse.cohortId || undefined,
        }
      });
    }
  }

  // Update watchItemId when startItem succeeds
  useEffect(() => {
    if (startItem.data?.watchItemId) {
      watchItemIdRef.current = startItem.data.watchItemId;
      setWatchItemId(startItem.data.watchItemId);
    }
  }, [startItem.data?.watchItemId, setWatchItemId]);

  // ✅ Call upsert watch time API every 15 seconds while video is playing
  useEffect(() => {
    const watchItemId = startItem.data?.watchItemId || currentCourse?.watchItemId;
    if (!watchItemId || !playing) return;
    const interval = setInterval(() => {
      upsertWatchTime.mutate({
        body: {
          watchItemId,
          itemId: currentCourse?.itemId!,
          cohortId: currentCourse?.cohortId || undefined,
        }
      } as any);
    }, 15000); // every 15 seconds
    return () => clearInterval(interval); // cleanup on unmount
  }, [startItem.data?.watchItemId, playing, currentCourse?.watchItemId]);

  const stopIfInProgressRef = useRef(stopIfInProgress);
  stopIfInProgressRef.current = stopIfInProgress;

  /**
   * Defense in depth: record an abandoned watch session on unmount, for any
   * path that leaves this item without calling stopItem() through the ref
   * above. A no-op if the session was already stopped (stopIfInProgress
   * checks progressStoppedRef), so this never double-fires against a normal
   * stop that already ran.
   *
   * Declared as its own effect with no dependencies, and before the
   * player-creation effect below, so its cleanup runs first on unmount and
   * reads progressStartedRef/watchItemIdRef while that effect's own cleanup
   * (which also runs on prop changes, not only unmount) still leaves them
   * intact.
   */
  useEffect(() => {
    return () => {
      void stopIfInProgressRef.current();
    };
  }, []);

  // Snapshot for the pagehide flush below, refreshed every render so the
  // handler (registered once) always reads current values without needing
  // currentCourse/seekForwardEnabled in its own dependency array.
  const pageHideContextRef = useRef({ currentCourse, seekForwardEnabled });
  pageHideContextRef.current = { currentCourse, seekForwardEnabled };

  /**
   * Best-effort delivery for the one case the unmount effect above cannot
   * cover: the tab or browser being closed outright, where the page's JS
   * stops running before a normal (non-keepalive) request can complete.
   *
   * Deliberately a raw `fetch` with keepalive rather than navigator.sendBeacon
   * — sendBeacon cannot set the Authorization header this API requires, since
   * it only ever sends as a plain POST with no custom headers, and this app
   * authenticates with a bearer token rather than a cookie. A keepalive fetch
   * is the browser-supported alternative that still survives page unload.
   *
   * No response is read and nothing is retried — the page may already be
   * gone by the time this returns, so this is fire-and-forget by design, the
   * same way sendBeacon would be.
   */
  const handlePageHide = useCallback(() => {
    const watchItemId = watchItemIdRef.current;
    const { currentCourse, seekForwardEnabled } = pageHideContextRef.current;
    if (
      !progressStartedRef.current ||
      progressStoppedRef.current ||
      stopInFlightRef.current ||
      !watchItemId ||
      !currentCourse
    ) {
      return;
    }

    const token = localStorage.getItem('firebase-auth-token');
    if (!token) return;

    const url =
      `${import.meta.env.VITE_BASE_URL}/users/progress/courses/${currentCourse.courseId}` +
      `/versions/${currentCourse.versionId ?? ''}/stop`;

    try {
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          watchItemId,
          itemId: currentCourse.itemId ?? '',
          moduleId: currentCourse.moduleId ?? '',
          sectionId: currentCourse.sectionId ?? '',
          seekForwardEnabled,
          nextItemId: nextItemIdRef.current,
          cohortId: currentCourse.cohortId || undefined,
        }),
      });
    } catch {
      // Best-effort — the tab is already closing, nothing left to do if this throws.
    }
  }, []);

  useEffect(() => {
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [handlePageHide]);

  const forceHighestQuality = (player: YTPlayerInstance) => {
    const qualities = player.getAvailableQualityLevels();
    // console.log("Qualities: ", qualities)

    if (!qualities || qualities.length === 0) return;

    if (qualities.includes('highres')) player.setPlaybackQuality('highres');
    else if (qualities.includes('hd1080')) player.setPlaybackQuality('hd1080');
    else if (qualities.includes('hd720')) player.setPlaybackQuality('hd720');
    else if (qualities.includes('large')) player.setPlaybackQuality('large');
  };

  // Load YouTube IFrame API
  useEffect(() => {
    if (!readyToDetect) return;

    /**
     * Player-ready work that is identical for both players. YouTube's own
     * handler adds iframe styling and captions on top; neither applies to HLS.
     */
    function handlePlayerReady(target: YTPlayerInstance) {
      const dur = target.getDuration();
      setPlayerReady(true);
      setDuration(dur);
      // Whatever renditions this player offers. Empty for YouTube until it has
      // buffered, which is fine — the control hides itself when there is nothing
      // to choose between.
      try {
        setQualityLevels(target.getAvailableQualityLevels?.() ?? []);
        setSelectedQuality(target.getPlaybackQuality?.() ?? 'auto');
      } catch {
        setQualityLevels([]);
      }
      target.setVolume(volume);
      setMaxTime(startTimeSeconds);
      target.seekTo(startTimeSeconds, true);
      onDurationChange?.(dur);
      target.pauseVideo();
    }

    /**
     * Progress lifecycle, shared by both players. Both emit the same numeric
     * state values (see PLAYER_STATE), so this needs no knowledge of which
     * player produced the event.
     */
    async function handlePlayerStateChange(event: {
      data: number;
      target: YTPlayerInstance;
    }) {
      if (event.data === PLAYER_STATE.PLAYING) {
        setPlaying(true);
        if (!progressStartedRef.current) {
          handleSendStartItem();
          setVideoEnded(false);
          progressStartedRef.current = true;
        }
        setTimeout(() => {
          forceHighestQuality(event.target);
        }, 500);
      } else if (event.data === PLAYER_STATE.ENDED) {
        setPlaying(false);
        setVideoEnded(true);
        if (!progressStoppedRef.current && currentCourse) {
          const watchItemId = watchItemIdRef.current || currentCourse.watchItemId;
          if (!watchItemId && isAlreadyWatched) {
            if (currentCourse.courseId === "6981df886e100cfe04f9c4ad") {
              console.log("Stop API failed for this course")
            } else {
              onNext?.();
            }
          }
          else if (watchItemId) {
            const success = await handleStopItem(watchItemId, 0); // No debounce on natural end
            if (success) {
              onNext?.();
            }
          }
        }
      } else {
        setPlaying(false);
      }
    }

    /**
     * Uploaded video is driven by an HLS-backed player exposing the same
     * interface, so everything below this branch — proctoring, seek gating,
     * watch-time, overlays — is shared rather than duplicated.
     */
    function createUploadedPlayer() {
      if (!iframeRef.current || !assetId) return;

      playerRef.current = createHlsPlayerInstance({
        container: iframeRef.current,
        resolveUrl: async () => (await getVideoPlaybackUrl(assetId)).url,
        startSeconds: startTimeSeconds,
        volume,
        events: {
          onReady: event => handlePlayerReady(event.target),
          onStateChange: event => void handlePlayerStateChange(event),
        },
      });
    }

    function createPlayer() {
      if (!iframeRef.current || !videoId) return;

      playerRef.current = new window.YT!.Player(iframeRef.current, {
        videoId,
        playerVars: {
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          fs: 0,
          iv_load_policy: 3,
          // cc_load_policy: 1,
          autohide: 1,
          showinfo: 0,
          playsinline: 1,
          cc_load_policy: subtitlesEnabled ? 1 : 0,
          cc_lang_pref: 'en',
          enablejsapi: 1,
          origin: window.location.origin,
          widget_referrer: window.location.origin,
          start: startTimeSeconds,
          end: 0,
          autoplay: 0,
        },
        events: {
          onReady: (event: { target: YTPlayerInstance }) => {
            // YouTube injects the iframe with a fixed default size (640x360);
            // force it to fill its container so the video scales with the panel.
            try {
              const iframeEl = (event.target as any).getIframe?.() as HTMLIFrameElement | undefined;
              if (iframeEl) {
                ytIframeRef.current = iframeEl;
                iframeEl.style.position = 'absolute';
                iframeEl.style.left = '0';
                iframeEl.style.width = '100%';
                iframeEl.style.display = 'block';
                applyIframeFraming(iframeEl, subtitlesEnabled);
              }
            } catch { /* getIframe unavailable — non-fatal */ }
            // Re-apply the persisted subtitle preference on a fresh player. This
            // goes through the same retrying path as the toggle: the captions
            // module is no more loaded at onReady than it is at click time.
            if (subtitlesEnabled) {
              playerRef.current = event.target;
              enableCaptions(event.target);
            }
            // Shared with the uploaded-video player: duration, volume, seek to
            // the segment start, then pause and wait for the camera.
            handlePlayerReady(event.target);
            console.log('✅ YouTube player ready - waiting for camera to be ready');

          },
          onStateChange: async (event: { data: number; target: YTPlayerInstance }) => {
            await handlePlayerStateChange(event);
          },
        },
      });
    }
    // An uploaded lesson needs no YouTube IFrame API at all, so it must not wait
    // on that script loading — which is also why player state is compared against
    // PLAYER_STATE rather than window.YT.PlayerState.
    if (isUploadedVideo) {
      createUploadedPlayer();
      return () => {
        if (playerRef.current) {
          playerRef.current.destroy?.();
          playerRef.current = null;
        }
      };
    }
    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = createPlayer;
    }

    // Cleanup when component unmounts or URL changes. Note this also fires on
    // videoId/startTimeSeconds/readyToDetect changes, not only on true unmount
    // — the actual "stop an abandoned session" logic lives in the dedicated
    // effect above instead, which runs only once, on unmount, before this one.
    return () => {
      // Clear any pending stop timeout
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      // Reset references
      progressStartedRef.current = false;
      progressStoppedRef.current = false;
      stopInFlightRef.current = false;
      watchItemIdRef.current = null;

      // Reset player ready state when video changes
      setPlayerReady(false);

      // Destroy player
      if (playerRef.current) {
        playerRef.current.destroy?.();
        playerRef.current = null;
      }
      ytIframeRef.current = null; // belongs to the destroyed player
    };
  }, [videoId, startTimeSeconds, readyToDetect]);


  // // Handle keyboard events including space for play/pause
  // useEffect(() => {

  //   if(!keyboardLockEnabled) return;
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     // Handle space key for play/pause
  //     if (e.code === 'Space') {
  //       e.preventDefault();
  //       e.stopPropagation();
  //       handlePlayPause();
  //       return;
  //     }

  //     const blockedKeys = [
  //       'KeyK', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  //       'KeyM', 'KeyF', 'KeyT', 'KeyC', 'Digit0', 'Digit1', 'Digit2', 'Digit3',
  //       'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9',
  //       'Period', 'Comma', 'KeyI', 'KeyO',
  //     ];

  //     if (blockedKeys.includes(e.code) ||
  //       (e.shiftKey && e.code === 'Period') ||
  //       (e.shiftKey && e.code === 'Comma')) {
  //       e.preventDefault();
  //       e.stopPropagation();
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyDown, true);
  //   return () => document.removeEventListener('keydown', handleKeyDown, true);
  // }, [handlePlayPause]);

  // Poll current time and enforce time constraints
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (playerReady) {
      interval = setInterval(async () => {

        if (progressStoppedRef.current || stopInFlightRef.current) {
          return;
        }

        const player = playerRef.current;
        if (player && player.getCurrentTime) {
          const time = player.getCurrentTime();
          setCurrentTime(time);
          setDuration(player.getDuration());
          // setVolume(player.getVolume());

          // Enforce startTime constraint
          if (time < startTimeSeconds) {
            if (!player) return;
            player.seekTo(startTimeSeconds, true);
            return;
          }

          // Enforce endTime constraint
          if (endTimeSeconds > 0 && !progressStoppedRef.current && !stopInFlightRef.current && time >= endTimeSeconds && currentCourse) {
            console.log("This if condition is triggred now -> ", "Fahhhhaaaa")
             const watchItemId = watchItemIdRef.current || currentCourse.watchItemId;

            // if (watchItemId) {
            player?.pauseVideo();

            // Check if user recently seeked (within last 3 seconds)
            const timeSinceLastSeek = Date.now() - lastSeekTimeRef.current;
            const debounceTime = timeSinceLastSeek < 3000 ? 2000 : 0;

            // CAPTURE tracking data BEFORE calling handleStopItem
            captureInProgressRef.current = true;
            const capturedTrackData = { ...watchTimeTrackRef.current };

            const success = await handleStopItem(watchItemId, debounceTime);
            if (success) {
              await sendWatchTimeTrackData(capturedTrackData);
              onNext?.();
            }
            // }
          }

          // Handle videos without endTime constraint that reach near completion
          if (endTimeSeconds === 0 && duration > 0 && !progressStoppedRef.current && !stopInFlightRef.current && time >= duration - 2 && currentCourse) {
            console.log("Fahhhhaaaaaaa")
            const watchItemId = watchItemIdRef.current || currentCourse.watchItemId;
            if (watchItemId) {
              player?.pauseVideo();

              // Check if user recently seeked
              const timeSinceLastSeek = Date.now() - lastSeekTimeRef.current;
              const debounceTime = timeSinceLastSeek < 3000 ? 2000 : 0;

              // CAPTURE tracking data BEFORE calling handleStopItem
              const capturedTrackData = { ...watchTimeTrackRef.current };

              const success = await handleStopItem(watchItemId, debounceTime);
              if (success) {
                await sendWatchTimeTrackData(capturedTrackData);
                onNext?.();
              }
            }
          }
          if (endTimeSeconds > 0 && time >= endTimeSeconds) {
            player.pauseVideo();
            if (!player) return;
            player.seekTo(endTimeSeconds, true);
            if (!videoEnded) {
              setVideoEnded(true);
            }
            return;
          }

          // Prevent forward seeking beyond what they've already watched
          // BUT allow forward seeking if either the video is completed OR seek forward is enabled in settings
          const speedTolerance = playbackRate * 1.0;
          const currentMaxTime = maxTimeRef.current; // Use ref for synchronous value
          const timeDifference = time - currentMaxTime;

          // Determine the effective end time (use duration if no end time is set)
          const effectiveEndTime = endTimeSeconds > 0 ? endTimeSeconds : duration;

          if (timeDifference > speedTolerance + 1.0 && time <= effectiveEndTime && !seekForwardEnabled) {
            if (!player) return;
            player.seekTo(currentMaxTime, true);
          } else if (time >= startTimeSeconds && (endTimeSeconds === 0 || time <= endTimeSeconds)) {
            const newMaxTime = Math.max(currentMaxTime, time);
            maxTimeRef.current = newMaxTime; // Update ref immediately
            setMaxTime(newMaxTime); // Update state for UI
          }
        }
      }, Math.max(200, 500 / playbackRate));
    }
    return () => clearInterval(interval);
  }, [playerReady, playbackRate, startTimeSeconds, endTimeSeconds, videoEnded]);

  useEffect(() => {
    if (!keyboardLockEnabled) return;

    const blockedKeys = new Set([
      't', 'i', 'o', 'k', 'f', 'c', 'm', ',', '.',
      'T', 'I', 'O', 'K', 'F', 'C', 'M', '<', '>'
    ]);

    const handler = (rawEvent: KeyboardEvent) => {
      try {
        const tgt = rawEvent.target as HTMLElement | null;

        // Allow typing inside input/textarea/contentEditable
        if (tgt) {
          const tag = tgt.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || (tgt as HTMLElement).isContentEditable) {
            return;
          }
        }

        // Space: only toggle play/pause, do NOT re-dispatch
        if (rawEvent.code === 'Space') {
          rawEvent.preventDefault();
          rawEvent.stopImmediatePropagation();
          handlePlayPause();
          return;
        }

        // Only handle keys in our blocked set
        if (!blockedKeys.has(rawEvent.key)) return;

        // For these keys, block default and re-dispatch a clean event
        rawEvent.preventDefault();
        rawEvent.stopImmediatePropagation();

        const synthetic = new KeyboardEvent('keydown', {
          key: rawEvent.key,
          code: rawEvent.code,
          bubbles: true,
          cancelable: true,
          shiftKey: rawEvent.shiftKey,
          ctrlKey: rawEvent.ctrlKey,
          altKey: rawEvent.altKey,
          metaKey: rawEvent.metaKey,
        });

        setTimeout(() => document.dispatchEvent(synthetic), 0);
      } catch (err) {
        console.error('Keyboard capture error', err);
      }
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [handlePlayPause, keyboardLockEnabled]);





  /**
   * The IFrame API exposes captions under two module names — 'cc' on the HTML5
   * player, 'captions' on the legacy one — and which one answers is not
   * something we get to know in advance. Both are tried; the one that returns a
   * tracklist is the live one.
   */
  const CAPTION_MODULES = ['cc', 'captions'] as const;

  /** The caption module that is actually answering, or null if none has yet. */
  const liveCaptionModule = (player: YTPlayerInstance): string | null => {
    for (const module of CAPTION_MODULES) {
      try {
        if (Array.isArray(player.getOption?.(module, 'tracklist'))) return module;
      } catch { /* module not loaded yet — try the next name */ }
    }
    return null;
  };

  /**
   * Turns captions on, retrying until the captions module is actually loaded.
   *
   * loadModule resolves asynchronously, so the setOption calls that follow it on
   * the next line act on a module that does not exist yet and are dropped — which
   * is why the toggle appeared to do nothing. Selecting the track has to be
   * retried until `tracklist` answers, not fired once and assumed to have landed.
   */
  const enableCaptions = (player: YTPlayerInstance) => {
    for (const module of CAPTION_MODULES) {
      try { player.loadModule?.(module); } catch { /* wrong name for this player */ }
    }

    let attemptsLeft = 12;
    const apply = () => {
      if (playerRef.current !== player) return; // player was swapped out from under us

      const module = liveCaptionModule(player);
      if (module) {
        const tracks = player.getOption?.(module, 'tracklist') as unknown[];
        if (tracks.length === 0) {
          // A definitive empty list: this video carries no caption track at all.
          toast.info('This video has no subtitles available');
          setSubtitlesEnabled(false);
          return;
        }
        player.setOption?.(module, 'track', { languageCode: 'en' });
        // Keep captions compact (see note in onReady).
        player.setOption?.(module, 'fontSize', -1);
        return;
      }

      // No answer yet. Keep waiting rather than reporting "no subtitles", since a
      // slow module load and a caption-less video look identical this early.
      if (--attemptsLeft > 0) captionProbeRef.current = setTimeout(apply, 400);
    };
    apply();
  };

  const handleToggleSubtitles = () => {
    const newState = !subtitlesEnabled;
    const player = playerRef.current;

    if (captionProbeRef.current) {
      clearTimeout(captionProbeRef.current);
      captionProbeRef.current = null;
    }

    if (player) {
      if (newState) {
        enableCaptions(player);
      } else {
        // Clearing the track is the documented "off"; the module that answers is
        // whichever one loaded, so clear both rather than guessing.
        for (const module of CAPTION_MODULES) {
          try { player.setOption?.(module, 'track', {}); } catch { /* not this one */ }
        }
      }
    }

    setSubtitlesEnabled(newState);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

 const playVideoAgain = () => {
  const player = playerRef.current;
  if (!player) return;

  // reset player actual position
  player.seekTo(startTimeSeconds, true);

  // force actual pause state first
  player.pauseVideo();

  // reset UI state
  setCurrentTime(startTimeSeconds);
  handleSendStartItem()
  setPlaying(false);
  setVideoEnded(false);

  // VERY IMPORTANT
  progressStartedRef.current = false;
  progressStoppedRef.current = false;
  stopInFlightRef.current = false;
  watchItemIdRef.current = null;

  // allow autoplay again
  hasAutoPlayedRef.current = false;

  // reset max seek tracking
  maxTimeRef.current = startTimeSeconds;
  setMaxTime(startTimeSeconds);
};

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: focusMode ? 'hsl(var(--stage))' : 'hsl(var(--background))',
        overflow: 'hidden',
        padding: focusMode ? '0px' : '16px',
        boxSizing: 'border-box',
        // Take the cursor away with the bar, so nothing floats over the picture.
        cursor: controlsOverlaid && !controlsVisible ? 'none' : 'pointer',
      }}
      onClick={handlePlayPause}
      onMouseMove={handleStageMouseMove}
      onMouseLeave={() => { pointerOverControlsRef.current = false; scheduleControlsHide(); }}
    >

      <ConfirmOverlay
        visible={isStopFailed}
        title="Failed to stop video"
        message={stopError}
        position="bottom-right"
        onCancel={() => setIsStopFailed(false)}
        onConfirm={() => {
          handleSkipItem();
          setIsStopFailed(false);
        }}
        handlePlayPause={()=>{playVideoAgain(); setIsStopFailed(false)}}
      />

      <NavigatingOverlay visible={isStopping || isSkipping} />

      <div
        ref={videoContainerRef}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: (focusMode || isFullscreen) ? '0' : '12px',
          overflow: 'hidden',
          position: 'relative',
        }}>
        {/* Video Container — overflow-hidden so the enlarged YouTube iframe
            (see onReady) is clipped, hiding YouTube's top/bottom chrome. */}
        <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden' }}>

          {!readyToDetect ? (  // Show preparing message before player is ready 
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'hsl(var(--muted))',
                borderRadius: '12px 12px 0 0',
              }}
            >
              <Loader2
                className="animate-spin"
                style={{
                  width: 40,
                  height: 40,
                  color: 'hsl(var(--primary))',
                  marginBottom: 18,
                }}
              />
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'hsl(var(--foreground))',
                  marginBottom: 14,
                }}
              >
                Preparing your environment...
              </div>
              <div
                key={quoteIndex}
                className="animate-in fade-in duration-700"
                style={{
                  fontSize: 15,
                  fontWeight: 400,
                  fontStyle: 'italic',
                  opacity: 0.75,
                  maxWidth: 460,
                  textAlign: 'center',
                  lineHeight: 1.5,
                  padding: '0 16px',
                }}
              >
                “{LOADING_QUOTES[quoteIndex]}”
              </div>
            </div>
          ) : (
            // YouTube iframe container
            <>
              <div

                ref={iframeRef}

                style={{

                  width: '100%',

                  height: '100%',

                  background: 'hsl(var(--background))',

                  borderRadius: '12px 12px 0 0',

                  overflow: 'hidden',

                  pointerEvents: 'none',

                  position: 'relative',

                  userSelect: 'none',

                  WebkitUserSelect: 'none',

                  MozUserSelect: 'none',

                  msUserSelect: 'none',

                }}

              />

              {/* With subtitles on, the iframe sits flush in its container so the
                  captions are not clipped (see applyIframeFraming) — which leaves
                  YouTube's title/share chrome inside the frame. Cover it. */}
              {subtitlesEnabled && subtitlesSupported && (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: YT_CHROME_COVER_PX,
                    background: '#000',
                    pointerEvents: 'none',
                    borderRadius: '12px 12px 0 0',
                  }}
                />
              )}

              {/* Multiple overlay layers to block YouTube controls */}

              {/* <div

            style={{

              position: 'absolute',

              top: 0,

              left: 0,

              right: 0,

              bottom: 0,

              background: 'transparent',

              pointerEvents: 'auto',

              zIndex: 10,

              userSelect: 'none',

              WebkitUserSelect: 'none',

              MozUserSelect: 'none',

              msUserSelect: 'none',

              cursor: 'pointer',

            }}

            onClick={(e) => {

              e.preventDefault();

              e.stopPropagation();

              handlePlayPause();

            }}

            onContextMenu={(e) => {

              e.preventDefault();

              e.stopPropagation();

            }}

            onDoubleClick={(e) => {

              e.preventDefault();

              e.stopPropagation();

            }}

            // onKeyDown={(e) => {

            //   e.preventDefault();

            //   e.stopPropagation();

            // }}


            tabIndex={-1}

          /> */}
              {/* /* add data-video-overlay so the focus-burring effect can detect it */}
              <div data-video-overlay="true"
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: 'transparent',
                  pointerEvents: 'auto',
                  zIndex: 10,
                  userSelect: 'none',
                  cursor: 'pointer',
                }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePlayPause(); }}
                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              />

              {/* YouTube's own top title/channel overlay is clipped away by the
                  enlarged-iframe trick (see onReady); this soft vignette just
                  polishes the top edge in the immersive stage while paused. */}
              {(focusMode || isFullscreen) && !playing && (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 88,
                    zIndex: 11,
                    pointerEvents: 'none',
                    background:
                      'linear-gradient(to bottom, hsl(var(--stage)) 0%, hsl(var(--stage)) 44%, hsl(var(--stage) / 0) 100%)',
                  }}
                />
              )}




              {/* Additional security overlay */}

              <div

                style={{

                  position: 'absolute',

                  top: 0,

                  left: 0,

                  right: 0,

                  bottom: 0,

                  background: 'rgba(0,0,0,0.001)',

                  pointerEvents: 'auto',

                  zIndex: 9,

                }}

                onContextMenu={(e) => e.preventDefault()}

                onDoubleClick={(e) => e.preventDefault()}

                onMouseDown={(e) => e.preventDefault()}

                onMouseUp={(e) => e.preventDefault()}

                onTouchStart={(e) => e.preventDefault()}

                onTouchEnd={(e) => e.preventDefault()}

              />



              {/* Anomaly Overlay */}

              {(rewindVid || doGesture || (pauseVid && !anomalies?.includes("faceCountDetection"))) && (

                // {(rewindVid || doGesture || pauseVid) && ( ----- to be uncommented later

                <div

                  className='shadow-2xl'

                  style={{

                    position: 'absolute',

                    top: 0,

                    left: 0,

                    right: 0,

                    bottom: 0,

                    background: 'rgba(70,50,0,0.85)',

                    zIndex: 20,

                    display: 'flex',

                    flexDirection: 'column',

                    alignItems: 'center',

                    justifyContent: 'center',

                    pointerEvents: 'auto',

                    borderRadius: '12px 12px 0 0',

                    transition: 'background 0.2s',

                  }}

                >

                  <div

                    className='shadow-2xl'

                    style={{

                      background: 'rgba(255, 255, 255, 1)',

                      borderRadius: 16,

                      padding: '32px 32px 24px 32px',

                      boxShadow: '0 4px 32px rgba(30,41,59,0.10)',

                      display: 'flex',

                      flexDirection: 'column',

                      alignItems: 'center',

                      justifyContent: 'center',

                      maxWidth: 400,

                      margin: '0 auto',

                    }}

                  >

                    <div

                      style={{

                        // background: '#fbbf24',

                        borderRadius: '50%',

                        width: 150,

                        height: 150,

                        display: 'flex',

                        alignItems: 'center',

                        justifyContent: 'center',

                        marginBottom: 18,

                        // boxShadow: '0 2px 12px rgba(30,41,59,0.10)',

                      }}

                    >

                      {/* Warning SVG icon */}

                      {(rewindVid || pauseVid) && (<svg

                        width="140"

                        height="140"

                        viewBox="0 0 156.262 144.407"

                        fill="none"

                        xmlns="http://www.w3.org/2000/svg"

                      >

                        <g transform="matrix(0.99073487,0,0,0.99073487,186.61494,2.4370252)">

                          <path

                            d="m -109.16602,7.2265625 c -0.13666,0.0017 -0.27279,0.017412 -0.40625,0.046875 -3.19494,0.029452 -6.17603,1.6944891 -7.78515,4.4824215 l -31.25,54.126953 -31.25,54.126958 h 0.002 c -3.41988,5.92217 1.01692,13.60908 7.85547,13.60937 h 62.5 62.501953 c 6.838552,-3.2e-4 11.277321,-7.68721 7.857422,-13.60937 l -31.25,-54.126958 -31.251955,-54.126953 c -1.46518,-2.5386342 -4.07917,-4.1634136 -6.97851,-4.4492184 -0.14501,-0.042788 -0.2944,-0.068998 -0.44532,-0.078125 h -0.004 c -0.0312,-0.00138 -0.0625,-0.00203 -0.0937,-0.00195 z"

                            style={{ fill: "#000" }}

                          />

                          <path

                            d="m -109.16545,9.2265625 c -2.63992,-0.1247523 -5.13786,1.2403375 -6.45899,3.5292965 l -31.25,54.126953 -31.25,54.126958 c -2.67464,4.63164 0.77657,10.60914 6.125,10.60937 h 62.5 62.50196 c 5.34844,-2.5e-4 8.79965,-5.97774 6.125,-10.60937 l -31.25,-54.126958 -31.25196,-54.126953 c -1.20213,-2.082863 -3.38689,-3.4150037 -5.78906,-3.5292965 h -0.002 z"

                            style={{ fill: "#fff" }}

                          />

                          <path

                            d="m -109.25919,11.224609 c -1.89626,-0.08961 -3.68385,0.887082 -4.63282,2.53125 l -31.25,54.126953 -31.25,54.126958 c -1.95283,3.38168 0.48755,7.6092 4.39258,7.60937 h 62.5 62.50196 c 3.905026,-1.8e-4 6.345394,-4.2277 4.39257,-7.60937 l -31.25,-54.126958 -31.25195,-54.126953 c -0.86311,-1.495461 -2.42763,-2.44919 -4.15234,-2.53125 z"

                            style={{ fill: "#000" }}

                          />

                          <path

                            d="m -46.997381,124.54655 -62.501079,0 -62.50108,0 31.25054,-54.127524 31.25054,-54.127522 31.25054,54.127521 z"

                            style={{ fill: "#df0000" }}

                          />

                          <g transform="translate(-188.06236)">

                            <circle

                              r="8.8173475"

                              cy="111.11701"

                              cx="78.564362"

                              style={{ fill: "#000" }}

                            />

                            <path

                              d="m 78.564453,42.955078 c -4.869714,-5.59e-4 -8.817839,3.946692 -8.818359,8.816406 3.15625,37.460938 0,0 3.15625,37.460938 8.93e-4,3.126411 2.535698,5.660342 5.662109,5.660156 3.126411,1.86e-4 5.661216,-2.533745 5.662109,-5.660156 3.154297,-37.460938 0,0 3.154297,-37.460938 -5.2e-4,-4.868951 -3.947455,-8.815886 -8.816406,-8.816406 z"

                              style={{ fill: "#000" }}

                            />

                          </g>

                        </g>

                      </svg>)}

                      {doGesture && !rewindVid && !pauseVid && (<img src="https://em-content.zobj.net/source/microsoft/309/thumbs-up_1f44d.png" className="w-auto h-full" />)}

                    </div>

                    <div

                      style={{

                        color: '#1e293b',

                        background: '#fbbf24',

                        padding: '14px 28px',

                        borderRadius: 10,

                        fontWeight: 1000,

                        fontSize: 21,

                        boxShadow: '0 30px 16px rgba(30,41,59,0.13)',

                        textAlign: 'center',

                        letterSpacing: 0.1,

                        maxWidth: 340,

                        lineHeight: 1.2,

                        border: '1px solid #f59e42',



                      }}

                    >

                      {rewindVid || pauseVid

                        ? (

                          <span style={{ fontWeight: 500, fontSize: 15 }}>

                            {anomalies?.includes("voiceDetection") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Don't speak!</strong>

                              </div>

                            ) : <></>}

                            {anomalies?.includes("noFace") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Please stay in frame</strong>

                              </div>

                            ) : anomalies?.includes("faceCountDetection") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Only one face!</strong>

                              </div>

                            ) : <></>}

                            {anomalies?.includes("blurDetection") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Keep your camera clear!</strong>

                              </div>

                            ) : <></>}

                            {anomalies?.includes("focus") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Stay focused!</strong>

                              </div>

                            ) : <></>}

                            {anomalies?.includes("faceRecognition") ? (

                              <div style={{ marginBottom: 6 }}>

                                <strong>Identity mismatch! Please ensure the registered student is watching.</strong>

                              </div>

                            ) : <></>}
                            
                            {anomalies?.includes("cameraIntegrity") ? (
                              <div style={{ marginBottom: 6 }}>
                                <strong>Camera issue detected. Please check your camera.</strong>
                              </div>
                            ) : <></>}

                          </span>

                        )

                        : (

                          <span style={{ fontWeight: 500, fontSize: 15 }}>

                            `Gesture Needed!`

                            <br />

                            <>

                              <span>

                                To continue watching your lesson, please show a <strong>thumbs up gesture</strong> in front of your camera.

                              </span>

                              <br />

                              <span style={{ fontWeight: 200, fontSize: 14 }}>

                                This helps us know you’re present and engaged. Once we detect your gesture, the video will resume automatically.

                              </span>

                            </></span>

                        )

                      }

                    </div>

                  </div>

                </div>

              )

              }


            </>
          )}
        </div>


        {/* Custom Controls — below the video normally, overlaid inside it in focus/fullscreen */}

        <div
          style={{
            padding: '3px 10px',
            borderTop: '1px solid hsl(var(--primary) / 0.2)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            ...(controlsOverlaid
              ? {
                  // Float the bar over the bottom of the video, and let it slide
                  // away while playing until the cursor comes looking for it.
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 20,
                  background: 'hsl(var(--card) / 0.82)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderRadius: 0,
                  opacity: controlsVisible ? 1 : 0,
                  transform: controlsVisible ? 'translateY(0)' : 'translateY(100%)',
                  pointerEvents: controlsVisible ? 'auto' : 'none',
                  transition: 'opacity 200ms ease, transform 220ms ease',
                }
              : {
                  background: 'hsl(var(--card))',
                  borderRadius: '0 0 12px 12px',
                }),
          }}
          onClick={(e) => e.stopPropagation()}
          // Resting on the bar holds it open; leaving it starts the countdown, so
          // the bar clears itself once you are done using it.
          onMouseEnter={() => { pointerOverControlsRef.current = true; clearControlsHideTimer(); setControlsVisible(true); }}
          onMouseLeave={() => { pointerOverControlsRef.current = false; scheduleControlsHide(); }}
        >
          {/* Progress Bar - Seeking controlled by seekForwardEnabled (fills the row middle) */}
          <div style={{ order: 2, flex: 1, minWidth: 140 }}>
            <Slider
              value={[currentTime]}
              min={startTimeSeconds}
              max={endTimeSeconds > 0 ? endTimeSeconds : duration}
              step={0.1}
              onValueChange={(value) => {
                const newTime = value[0];
                const currentTimeStr = formatSecondsToTime(currentTime);
                const newTimeStr = formatSecondsToTime(newTime);

                // Record seek time 
                lastSeekTimeRef.current = Date.now();

                // Track seek as rewind or fast forward
                if (newTime < currentTime) {
                  setWatchTimeTrack(prev => ({
                    ...prev,
                    rewinds: prev.rewinds + 1,
                    rewindData: [...prev.rewindData, {
                      from: currentTimeStr,
                      to: newTimeStr,
                      createdAt: new Date().toISOString()
                    }]
                  }));
                } else if (newTime > currentTime) {
                  // Fast forward
                  setWatchTimeTrack(prev => ({
                    ...prev,
                    fastForwards: prev.fastForwards + 1,
                    fastForwardData: [...prev.fastForwardData, {
                      from: currentTimeStr,
                      to: newTimeStr,
                      createdAt: new Date().toISOString()
                    }]
                  }));
                }

                // If seekForward is disabled and user tries to seek forward
                if (!seekForwardEnabled && newTime > currentTime) {
                  // Throttle toast to prevent spam (max once every 2 seconds)
                  const now = Date.now();
                  if (now - lastSeekErrorToastRef.current > 2000) {
                    toast.error('You are not allowed to seek forward');
                    lastSeekErrorToastRef.current = now;
                  }
                  return;
                }

                // Allow seeking (backward always, forward only if enabled)
                if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                  playerRef.current.seekTo(newTime, true);
                } else {
                  console.log('⚠️ [DEBUG] Player not ready or seekTo not available');
                }

                // Cancel any pending stop request when user is actively seeking
                if (stopTimeoutRef.current) {
                  clearTimeout(stopTimeoutRef.current);
                  stopTimeoutRef.current = null;
                }
              }}
              className="w-full"
            />
          </div>

            {/* Left Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, order: 1 }}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
                size="icon"
                variant={playing ? "default" : "secondary"}
                className="rounded-full w-8 h-8 flex-shrink-0"
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBackward();
                }}
                size="icon"
                variant="secondary"
                className="rounded-full w-8 h-8 flex-shrink-0"
                aria-label="Back 10 seconds"
              >
                <SkipBack className="h-4 w-4" />
              </Button>

              {/* Forward seek button - shown when seekForwardEnabled is true */}
              {seekForwardEnabled && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleForward();
                  }}
                  size="icon"
                  variant="secondary"
                  className="rounded-full w-8 h-8 flex-shrink-0"
                  aria-label="Forward 10 seconds"
                  title="Forward 10 seconds"
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              )}

              <span style={{
                color: 'hsl(var(--foreground))',
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                fontWeight: 600,
                minWidth: 62,
                textShadow: '0 1px 3px hsl(var(--background) / 0.5)',
                flexShrink: 0
              }}>
                {formatTime(Math.max(startTimeSeconds, currentTime))} / {formatTime(endTimeSeconds > 0 ? endTimeSeconds : duration)}
              </span>
            </div>

            {/* Right Controls */}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, order: 3 }}>

              <TooltipProvider>
                {/* Subtitles — hidden entirely for players that cannot show any. */}
                {subtitlesSupported && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleToggleSubtitles}
                      variant="ghost"
                      size="icon"
                      className={`rounded-sm relative group h-8 w-8 transition-colors duration-200 ${subtitlesEnabled
                        ? "text-black dark:text-white"
                        : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                        }`}
                    >
                      <span className="flex items-center justify-center">
                        <Captions className="h-5 w-5" strokeWidth={2.25} />
                      </span>

                      {subtitlesEnabled && (
                        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-[2px] bg-red-500 rounded-full"></span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Toggle Subtitles</p>
                  </TooltipContent>
                </Tooltip>
                )}

                {/* Speed Control */}
                <Card className="flex flex-row items-center gap-1.5 px-2 py-0.5 bg-accent/15 flex-shrink-0">
                  <span className="hidden md:block text-xs font-semibold text-foreground min-w-[24px]">
                    Speed
                  </span>
                  <FastForward className="flex md:hidden h-3.5 w-3.5 text-accent flex-shrink-0" />
                  <Slider
                    value={[playbackRate]}
                    min={0.25}
                    max={2}
                    step={0.05}
                    onValueChange={(value) => {
                      const rate = value[0];
                      const player = playerRef.current;
                      if (player && typeof player.getAvailablePlaybackRates === 'function') {
                        const availableRates = player.getAvailablePlaybackRates!();
                        let closest = availableRates[0];
                        for (const r of availableRates) {
                          if (Math.abs(r - rate) < Math.abs(closest - rate)) closest = r;
                        }
                        player.setPlaybackRate(closest);
                        // Update both local state and global store
                        setPlaybackRate(closest);
                      } else {
                        playerRef.current?.setPlaybackRate(rate);
                        // Update both local state and global store
                        setPlaybackRate(rate);
                      }
                    }}
                    className="w-[80px]"
                  />
                  <span className="text-xs font-semibold text-primary min-w-[24px] text-center">
                    {playbackRate.toFixed(2)}x
                  </span>
                </Card>

                {/*
                  * Quality selector. Rendered only when the active player reports
                  * renditions, so it appears for uploaded video (HLS ladder) and
                  * stays hidden where there is nothing to choose between.
                  */}
                {qualityLevels.length > 1 && (
                  <Card className="flex flex-row items-center gap-1.5 px-2 py-0.5 bg-accent/15 flex-shrink-0">
                    <span className="hidden md:block text-xs font-semibold text-foreground">
                      Quality
                    </span>
                    <select
                      value={selectedQuality}
                      onChange={e => {
                        const next = e.target.value;
                        setSelectedQuality(next);
                        playerRef.current?.setPlaybackQuality?.(next);
                      }}
                      aria-label="Video quality"
                      className="bg-transparent text-xs font-semibold text-primary
                        outline-none cursor-pointer"
                    >
                      {qualityLevels.map(level => (
                        <option key={level} value={level}>
                          {level === 'auto' ? 'Auto' : level}
                        </option>
                      ))}
                    </select>
                  </Card>
                )}

                {/* Volume Control */}
                <Card className="flex flex-row items-center gap-1.5 px-2 py-0.5 bg-accent/15 flex-shrink-0">
                  <Volume2 className="h-3.5 w-3.5 text-accent flex-shrink-0" />
                  <Slider
                    value={[volume]}
                    min={0}
                    max={100}
                    onValueChange={(value) => {
                      const v = value[0];
                      setVolume(v);
                      playerRef.current?.setVolume(v);
                    }}
                    className="w-[80px]"
                  />
                  <span className="text-xs font-semibold text-foreground min-w-[24px] text-center">
                    {Math.round(volume)}%
                  </span>
                </Card>

                {/* Fullscreen Toggle */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFullscreen();
                      }}
                      variant="ghost"
                      size="icon"
                      className="rounded-sm relative group h-8 w-8 transition-colors duration-200 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                      aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    >
                      <span className="flex items-center justify-center">
                        {isFullscreen ? (
                          <Minimize className="h-5 w-5" strokeWidth={2.25} />
                        ) : (
                          <Maximize className="h-5 w-5" strokeWidth={2.25} />
                        )}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

          {/* Next Lesson Button */}
          {onNext && (
            <div style={{
              borderTop: '1px solid hsl(var(--border))',
              paddingTop: '12px',
              marginTop: '12px',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onNext();
                }}
                disabled={isProgressUpdating}
                className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground border-0"
                size="lg"
              >
                {isProgressUpdating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground mr-2" />
                    Processing
                  </>
                ) : (
                  <>
                    Next Lesson
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Global CSS to block YouTube interface */}
      <style>{`
        iframe[src*="youtube.com"] {
          pointer-events: none !important;
        }
        
        /* Hide any YouTube branding or controls that might appear */
        .ytp-chrome-top,
        .ytp-chrome-bottom,
        .ytp-gradient-top,
        .ytp-gradient-bottom,
        .ytp-progress-bar-container,
        .ytp-chrome-controls,
        .ytp-title,
        .ytp-watermark,
        .ytp-menuitem,
        .ytp-popup,
        .ytp-settings-menu,
        .ytp-panel,
        .annotation,
        .video-annotations,
        .ytp-cards-teaser,
        .ytp-ce-element {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        
        /* Block right-click on the entire video area */
        .video-container * {
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          -khtml-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
      `}</style>
    </div >
  );
});

Video.displayName = 'Video';

export default Video;

type NavigatingOverlayProps = {
  visible: boolean;
  title?: string;
  message?: string;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  variant?: 'blue' | 'red' | 'green';
};

export function NavigatingOverlay({
  visible,
  title = 'Please wait',
  message = 'Navigating to next item…',
  position = 'top-right',
  variant = 'blue',
}: NavigatingOverlayProps) {
  if (!visible) return null;

  const positionClasses: Record<typeof position, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const variantClasses: Record<
    typeof variant,
    {
      card: string;
      badge: string;
      icon: string;
    }
  > = {
    blue: {
      card: 'border-blue-400/40 bg-blue-600/95 text-blue-50',
      badge: 'border-blue-50/30 bg-blue-50/10 text-blue-50',
      icon: 'bg-blue-50/10',
    },
    red: {
      card: 'border-red-400/40 bg-red-600/95 text-red-50',
      badge: 'border-red-50/30 bg-red-50/10 text-red-50',
      icon: 'bg-red-50/10',
    },
    green: {
      card: 'border-green-400/40 bg-green-600/95 text-green-50',
      badge: 'border-green-50/30 bg-green-50/10 text-green-50',
      icon: 'bg-green-50/10',
    },
  };

  const styles = variantClasses[variant];

  return (
    <div
      className={`absolute z-50 animate-in slide-in-from-right-3 duration-300 ${positionClasses[position]}`}
    >
      <Card className={`shadow-lg backdrop-blur-md ${styles.card}`}>
        <CardContent className="flex items-center gap-3 px-4 py-3">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded ${styles.icon}`}
          >
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>

          <div className="flex-1 space-y-1">
            <Badge
              variant="outline"
              className={`font-semibold ${styles.badge}`}
            >
              {title}
            </Badge>

            <p className="text-sm font-medium leading-relaxed">
              {message}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
interface ConfirmOverlayProps {
  visible: boolean;
  title: string;
  message: string;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  onCancel: () => void;
  onConfirm: () => void;
  handlePlayPause: ()=> void
}

export function ConfirmOverlay({
  visible,
  title,
  message,
  position = "bottom-right",
  onCancel,
  onConfirm,
  handlePlayPause
}: ConfirmOverlayProps) {
  if (!visible) return null;

  const positionClasses: Record<typeof position, string> = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  return (
    <div
      className={`absolute z-50 animate-in slide-in-from-right-3 duration-300 ${positionClasses[position]}`}
    >
      <Card className={`border-red-400/40 ${message === "Invalid watch time" ? "bg-yellow-600/95": "bg-red-600/95"} text-red-50 shadow-lg backdrop-blur-md w-80`}>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${message === "Invalid watch time" ? "bg-yellow-50/10": "bg-red-50/10"}`}>
              <XCircle className="h-6 w-6 text-red-50" />
            </div>
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold text-red-50">{title}</p>
              <p className="text-sm text-red-50/90">{message === "Invalid watch time" ? "Invalid watch time. Video need to be watched for 30 seconds. Please restart....": message}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              className="border-red-200/30 bg-transparent text-red-50 hover:bg-red-50/10 hover:text-red-50"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red-50 text-red-600 hover:bg-white hover:text-red-700 font-semibold"
              onClick={message === "Invalid watch time" ? handlePlayPause : onConfirm}
            >
              {message === "Invalid watch time" ? "OK" : "Continue"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}